import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateNotificationModal from './components/CreateNotificationModal';
import axiosInstance from '../../utils/axiosInstance';
import { useAuth } from '../../context/AuthContext';
import { canAccess } from '../../utils/permissions';

const PushNotifications = () => {
  const { user: currentUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [patients, setPatients] = useState([]);
  const [stats, setStats] = useState({
    totalSent: 0,
    totalScheduled: 0,
    totalDrafts: 0
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 10
  });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'push', // 'push', 'whatsapp', or 'both'
    audience: 'all', // 'all' or 'selected_patients'
    selectedPatients: [],
    schedule: false,
    scheduledAt: '',
    imageUrl: '',
    deepLink: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // API base URL
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;



  // Fetch notifications with filters and pagination
  const fetchNotifications = async (page = 1, status = '') => {
    try {
      setIsLoading(true);
      const params = {
        page,
        limit: 10,
        status: activeTab === 'all' ? '' : activeTab
      };

      const response = await axiosInstance.get(`${API_BASE_URL}/api/notifications`, {
        withCredentials: true, 
        params
      });

      setNotifications(response.data.notifications || []);
      setPagination(response.data.meta || {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        perPage: 10
      });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      alert(error.response?.data?.error || 'Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch patients for selection
  const fetchPatients = async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/api/patients`, {
        withCredentials: true,
        params: {
          page: 1,
          limit: 100
        }
      });
      setPatients(response.data.patients || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/api/notifications/stats`, {
        withCredentials: true
      });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchNotifications(1);
    fetchPatients();
    fetchStats();
  }, [activeTab]); // Refetch when tab changes

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePatientSelection = (patientId) => {
    setFormData(prev => ({
      ...prev,
      selectedPatients: prev.selectedPatients.includes(patientId)
        ? prev.selectedPatients.filter(id => id !== patientId)
        : [...prev.selectedPatients, patientId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.audience === 'selected_patients' && formData.selectedPatients.length === 0) {
      alert('Please select at least one patient');
      return;
    }

    if (formData.type === 'whatsapp' && formData.message.length > 1000) {
      alert('WhatsApp messages cannot exceed 1000 characters');
      return;
    }

    if (formData.type === 'push' && formData.message.length > 240) {
      alert('Push notifications cannot exceed 240 characters');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const notificationData = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        audience: formData.audience,
        selectedPatients: formData.audience === 'selected_patients' ? formData.selectedPatients : [],
        schedule: formData.schedule,
        scheduledAt: formData.schedule ? formData.scheduledAt : null,
        imageUrl: formData.imageUrl || null,
        deepLink: formData.deepLink || null
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/notifications`,
        notificationData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setShowCreateModal(false);
        resetForm();
        fetchNotifications();
        fetchStats();
        alert(response.data.message);
      } else {
        alert(response.data.error || 'Failed to create notification');
      }
      
    } catch (error) {
      console.error('Error creating notification:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create notification';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'push',
      audience: 'all',
      selectedPatients: [],
      schedule: false,
      scheduledAt: '',
      imageUrl: '',
      deepLink: ''
    });
  };

  const sendNotificationNow = async (notificationId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/notifications/${notificationId}/send`,
        {}, // Empty data object
        {
          withCredentials: true
        }
      );

      if (response.data.success) {
        fetchNotifications();
        fetchStats();
        alert('Notification sent successfully!');
      } else {
        alert(response.data.error || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send notification';
      alert(errorMessage);
    }
  };

  const resendNotification = async (notificationId, sendToFailedOnly = false) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/notifications/${notificationId}/resend`,
        { sendToFailedOnly },
        {
          withCredentials: true
        }
      );

      if (response.data.success) {
        fetchNotifications();
        fetchStats();
        alert(response.data.message);
      } else {
        alert(response.data.error || 'Failed to resend notification');
      }
    } catch (error) {
      console.error('Error resending notification:', error);
      const errorMessage = error.response?.data?.error || 'Failed to resend notification';
      alert(errorMessage);
    }
  };

  const duplicateNotification = async (notificationId) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/notifications/${notificationId}/duplicate`,
        {},
        {
          withCredentials: true
        }
      );

      if (response.data.success) {
        fetchNotifications();
        alert('Notification duplicated successfully!');
      } else {
        alert(response.data.error || 'Failed to duplicate notification');
      }
    } catch (error) {
      console.error('Error duplicating notification:', error);
      const errorMessage = error.response?.data?.error || 'Failed to duplicate notification';
      alert(errorMessage);
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/api/notifications/${notificationId}`, {
        withCredentials: true
      });

      if (response.data.success) {
        fetchNotifications();
        fetchStats();
        alert('Notification deleted successfully!');
      } else {
        alert(response.data.error || 'Failed to delete notification');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete notification';
      alert(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-50 text-green-700 border border-green-200';
      case 'scheduled':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'draft':
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      case 'partial':
        return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
      case 'failed':
        return 'bg-red-50 text-red-700 border border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'push':
        return 'bg-purple-50 text-purple-700 border border-purple-200';
      case 'whatsapp':
        return 'bg-green-50 text-green-700 border border-green-200';
      case 'both':
        return 'bg-indigo-50 text-indigo-700 border border-indigo-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'push':
        return '📱';
      case 'whatsapp':
        return '💬';
      case 'both':
        return '📱💬';
      default:
        return '📢';
    }
  };

  const getAudienceBadge = (audience) => {
    switch (audience) {
      case 'all':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'selected_patients':
        return 'bg-pink-50 text-pink-700 border border-pink-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays} days ago`;
    }
    
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: new Date().getFullYear() !== date.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredNotifications = notifications;

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchNotifications(page);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} />
    <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="p-2 sm:p-4">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <div className="w-full sm:w-auto">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Push Notifications</h1>
              <p className="text-xs text-gray-600 hidden sm:block">
                Manage and send push notifications & WhatsApp messages to users
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              disabled={!canAccess(currentUser, "canCreate")}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm px-3 py-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Plus" size={14} className="mr-1 sm:mr-2" />
              Create Notification
            </Button>
          </div>

          {/* Stats Cards - REMOVED OPEN RATE */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-medium">Total Sent</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 mt-1">{stats.totalSent || 0}</p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon name="Send" size={14} className="sm:text-lg text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-medium">Scheduled</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 mt-1">{stats.totalScheduled || 0}</p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Icon name="Clock" size={14} className="sm:text-lg text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-medium">Drafts</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 mt-1">{stats.totalDrafts || 0}</p>
                </div>
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <Icon name="FileText" size={14} className="sm:text-lg text-gray-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto px-2 sm:px-4 no-scrollbar">
                {[
                  { key: 'all', label: 'All', fullLabel: 'All Notifications' },
                  { key: 'sent', label: 'Sent', fullLabel: 'Sent' },
                  { key: 'scheduled', label: 'Scheduled', fullLabel: 'Scheduled' },
                  { key: 'draft', label: 'Drafts', fullLabel: 'Drafts' },
                  { key: 'partial', label: 'Partial', fullLabel: 'Partial' },
                  { key: 'failed', label: 'Failed', fullLabel: 'Failed' }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`whitespace-nowrap py-3 px-2 sm:px-1 border-b-2 font-medium text-xs ${
                      activeTab === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="sm:hidden">{tab.label}</span>
                    <span className="hidden sm:inline">{tab.fullLabel}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Notifications List */}
            <div className="p-2 sm:p-4">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 text-sm mt-2">Loading notifications...</p>
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Bell" size={32} className="sm:text-5xl text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No notifications found</p>
                  <p className="text-gray-400 text-xs mt-1">
                    {activeTab === 'all' 
                      ? 'Create your first notification to get started' 
                      : `No ${activeTab} notifications`
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {filteredNotifications.map((notification) => (
                    <div key={notification.id} className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2">
                            <h3 className="text-sm font-semibold text-gray-900 truncate">
                              {notification.isResend && '↻ '}
                              {notification.title}
                            </h3>
                            <div className="flex flex-wrap gap-1 sm:gap-2">
                              <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadge(notification.type)}`}>
                                <span className="sm:hidden">{getTypeIcon(notification.type)}</span>
                                <span className="hidden sm:inline">
                                  {getTypeIcon(notification.type)} {notification.type}
                                </span>
                              </span>
                              <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(notification.status)}`}>
                                <span className="capitalize">{notification.status}</span>
                              </span>
                              <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium ${getAudienceBadge(notification.audience)}`}>
                                <span className="sm:hidden">
                                  {notification.audience === 'selected_patients' ? 'Selected' : 'All'}
                                </span>
                                <span className="hidden sm:inline">
                                  {notification.audience.replace('_', ' ')}
                                </span>
                              </span>
                              {notification.isResend && (
                                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  Resend
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-600 mb-2 sm:mb-3 line-clamp-2 sm:line-clamp-none">{notification.message}</p>
                          
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Icon name="Users" size={12} />
                              <span>{notification.recipients || 0} recipients</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Icon name="Calendar" size={12} />
                              <span className="truncate max-w-[120px] sm:max-w-none">
                                {notification.status === 'sent' && notification.sentAt
                                  ? `Sent ${formatDate(notification.sentAt)}`
                                  : notification.status === 'scheduled' && notification.scheduledAt
                                  ? `Scheduled ${formatDate(notification.scheduledAt)}`
                                  : notification.status === 'draft'
                                  ? `Created ${formatDate(notification.createdAt)}`
                                  : formatDate(notification.createdAt)
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-start sm:items-end space-y-1 sm:space-y-2 sm:ml-4">
                          <div className="flex flex-wrap gap-1 sm:gap-1">
                            {notification.status === 'draft' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-800 text-xs px-2 py-1 h-7"
                                onClick={() => sendNotificationNow(notification.id)}
                              >
                                <Icon name="Send" size={12} className="mr-1" />
                                <span className="sm:inline hidden">Send Now</span>
                                <span className="sm:hidden">Send</span>
                              </Button>
                            )}
                            {(notification.status === 'sent' || notification.status === 'partial') && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 h-7"
                                  onClick={() => resendNotification(notification.id)}
                                >
                                  <Icon name="RefreshCw" size={12} className="mr-1" />
                                  <span className="sm:inline hidden">Resend All</span>
                                  <span className="sm:hidden">All</span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-orange-600 hover:text-orange-800 text-xs px-2 py-1 h-7"
                                  onClick={() => resendNotification(notification.id, true)}
                                >
                                  <Icon name="RefreshCw" size={12} className="mr-1" />
                                  <span className="sm:inline hidden">Resend Failed</span>
                                  <span className="sm:hidden">Failed</span>
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-600 hover:text-gray-800 text-xs px-2 py-1 h-7"
                              onClick={() => duplicateNotification(notification.id)}
                            >
                              <Icon name="Copy" size={12} className="mr-1" />
                              <span className="sm:inline hidden">Duplicate</span>
                              <span className="sm:hidden">Copy</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!canAccess(currentUser, "canDelete")}
                              className="text-red-600 hover:text-red-800 text-xs px-2 py-1 h-7 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => canAccess(currentUser, "canDelete") && deleteNotification(notification.id)}
                            >
                              <Icon name="Trash2" size={12} className="mr-1" />
                              <span className="sm:inline hidden">Delete</span>
                              <span className="sm:hidden">Delete</span>
                            </Button>
                          </div>
                          {notification.createdBy && (
                            <div className="text-xs text-gray-500 mt-1 sm:mt-2 truncate max-w-[150px] sm:max-w-none">
                              <span className="sm:hidden">By: {notification.createdBy.name?.split(' ')[0]}</span>
                              <span className="hidden sm:inline">Created by: {notification.createdBy.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
                  <div className="text-xs sm:text-sm text-gray-700 mb-2 sm:mb-0">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="text-xs h-8 px-2"
                    >
                      <span className="sr-only sm:not-sr-only">Previous</span>
                      <Icon name="ChevronLeft" size={12} className="sm:hidden" />
                    </Button>
                    <div className="hidden sm:flex space-x-1">
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={pagination.currentPage === pageNum ? "primary" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className={`text-xs h-8 w-8 p-0 ${
                              pagination.currentPage === pageNum 
                                ? "bg-blue-600 text-white" 
                                : "text-gray-700"
                            }`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <div className="sm:hidden text-xs text-gray-700 px-2">
                      {pagination.currentPage} / {pagination.totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="text-xs h-8 px-2"
                    >
                      <span className="sr-only sm:not-sr-only">Next</span>
                      <Icon name="ChevronRight" size={12} className="sm:hidden" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Create Notification Modal - SEPARATED INTO COMPONENT */}
      {showCreateModal && (
        <CreateNotificationModal
          showModal={showCreateModal}
          setShowModal={setShowCreateModal}
          formData={formData}
          setFormData={setFormData}
          patients={patients}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          handleInputChange={handleInputChange}
          handlePatientSelection={handlePatientSelection}
          resetForm={resetForm}
        />
      )}
    </div>
  );
};

export default PushNotifications;